/**
 * Governance escalation store.
 *
 * Persistent escalation inbox backed by governance-service REST — unlike the
 * WebSocket governance alerts in useExecutionStore, these survive reloads and
 * are visible even when the escalation fired while the user was offline.
 *
 * Endpoints (same as admin-ui's escalation.service.ts):
 *   GET  /v1/governance/escalations
 *   GET  /v1/governance/escalations/pending/count
 *   GET  /v1/governance/escalations/{id}
 *   POST /v1/governance/escalations/{id}/resolve
 *   POST /v1/governance/escalations/{id}/answer   (question escalations, agion/Agion#3427)
 *   POST /v1/governance/escalations/{id}/answer-ticket   (free-text and form answers go to the run)
 *   POST /v1/governance/escalations/{id}/accept   (take over a hold, agion/Agion#3435)
 *   POST /v1/governance/escalations/{id}/return   (hand a hold back, agion/Agion#3435)
 */

import { useMemo } from 'react'
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import type {
  AnswerEscalationRequest,
  AnswerRefusal,
  AnswerTicket,
  Escalation,
  EscalationAnswer,
  EscalationListRequest,
  EscalationListResponse,
  ResolveEscalationRequest,
} from '../types/escalations'
import { apiClient } from '../services/api'
import { answerRefusalOf } from '../services/escalation-answer'
import {
  answerGoesToRun,
  connectForAnswer,
  relayAnswerTiming,
  relayPause,
  sendAnswerToRun,
} from '../services/escalation-answer-relay'
import { takeOverRefusalOf, type TakeOverRefusal } from '../services/operator-takeover'

// =============================================================================
// ESCALATION API
// =============================================================================

export const escalationApi = {
  async list(filters?: EscalationListRequest): Promise<EscalationListResponse> {
    const params = new URLSearchParams()
    if (filters?.status) params.append('status', filters.status)
    if (filters?.agent_id) params.append('agent_id', filters.agent_id)
    if (filters?.mission_id) params.append('mission_id', filters.mission_id)
    if (filters?.limit) params.append('limit', String(filters.limit))
    if (filters?.offset) params.append('offset', String(filters.offset))

    const query = params.toString()
    const endpoint = `/v1/governance/escalations${query ? `?${query}` : ''}`
    const response = await apiClient.get<EscalationListResponse>(endpoint)
    return {
      escalations: Array.isArray(response?.escalations) ? response.escalations : [],
      total: response?.total ?? 0,
      limit: response?.limit ?? 50,
      offset: response?.offset ?? 0,
    }
  },

  async getPendingCount(): Promise<number> {
    const result = await apiClient.get<{ count: number }>(
      '/v1/governance/escalations/pending/count',
    )
    return result?.count ?? 0
  },

  async resolve(id: string, request: ResolveEscalationRequest): Promise<void> {
    await apiClient.post<void>(`/v1/governance/escalations/${id}/resolve`, request)
  },

  async get(id: string): Promise<Escalation | undefined> {
    return apiClient.get<Escalation>(`/v1/governance/escalations/${id}`)
  },

  /** Governance answers with the escalation as stored, status "answered". */
  async answer(id: string, request: AnswerEscalationRequest): Promise<Escalation | undefined> {
    return apiClient.post<Escalation>(`/v1/governance/escalations/${id}/answer`, request)
  },

  /**
   * A ticket for an answer that goes to the run (free text or form): governance
   * names the answering user and the run in it and never sees the answer.
   */
  async answerTicket(id: string): Promise<AnswerTicket | undefined> {
    return apiClient.post<AnswerTicket>(`/v1/governance/escalations/${id}/answer-ticket`, {})
  },

  /** Takes over a pending chat handoff; governance names the caller its operator. */
  async accept(id: string): Promise<void> {
    await apiClient.post<unknown>(`/v1/governance/escalations/${id}/accept`, {})
  },

  /** Hands an accepted chat handoff back to the agent. */
  async returnHandoff(id: string): Promise<void> {
    await apiClient.post<unknown>(`/v1/governance/escalations/${id}/return`, {})
  },
}

/** A hold: a chat handoff the runtime created for one run (agion/Agion#3435). */
export function isOperatorHold(escalation: Escalation): boolean {
  return escalation.escalation_type === 'chat_handoff' && Boolean(escalation.execution_id) && Boolean(escalation.mission_id)
}

/** The stored escalation with the server's fresh fields over it. */
function mergeEscalation(escalations: Escalation[], fresh: Escalation): Escalation[] {
  return escalations.map((e) => (e.id === fresh.id ? { ...e, ...fresh } : e))
}

function isEscalation(value: unknown): value is Escalation {
  return Boolean(value) && typeof value === 'object' && typeof (value as Escalation).id === 'string'
}

/** The stored escalation with the server's fresh fields over it, added when a poll dropped it. */
function upsertEscalation(escalations: Escalation[], fresh: Escalation): Escalation[] {
  return escalations.some((e) => e.id === fresh.id) ? mergeEscalation(escalations, fresh) : [...escalations, fresh]
}

/**
 * Sends an answer whose content goes to the run (free text or form; owner ruling
 * 2026-10-25): the status socket connected, a ticket from governance, the
 * answer through the relay, then
 * governance is read until the run has settled it. Each read goes to onFresh.
 * Resolves with the refusal, or undefined once the escalation is no longer
 * pending. The answer is passed on, never kept or logged.
 */
async function answerOnTheRun(
  id: string,
  answer: EscalationAnswer,
  onFresh: (fresh: Escalation) => void,
): Promise<AnswerRefusal | undefined> {
  // Connect first: the ticket's life starts when governance issues it.
  if (!(await connectForAnswer())) return 'unreachable'

  let ticket: AnswerTicket | undefined
  try {
    ticket = await escalationApi.answerTicket(id)
  } catch (error) {
    // The ticket request carries no answer, so its error holds none.
    console.error('Failed to get an answer ticket:', error)
    return answerRefusalOf(error)
  }
  if (typeof ticket?.ticket !== 'string' || ticket.ticket === '') return 'failed'

  const sent = await sendAnswerToRun(id, ticket.ticket, answer)
  if (!sent.delivered) return sent.refusal

  for (let read = 0; read < relayAnswerTiming.pollAttempts; read++) {
    if (read > 0) await relayPause(relayAnswerTiming.pollIntervalMs)
    let fresh: Escalation | undefined
    try {
      fresh = await escalationApi.get(id)
    } catch {
      // The next read tries again.
      continue
    }
    if (!isEscalation(fresh)) continue
    onFresh(fresh)
    if (fresh.status !== 'pending') return undefined
  }
  return 'unconfirmed'
}

// =============================================================================
// ZUSTAND STORE
// =============================================================================

export interface EscalationStore {
  escalations: Escalation[]
  pendingCount: number
  isLoading: boolean
  error: string | null
  /** Escalation IDs with an in-flight resolve call */
  resolvingIds: Set<string>
  /** Escalation IDs with an in-flight answer call */
  answeringIds: Set<string>
  /** The last refusal of an answer, per escalation ID */
  answerRefusals: Record<string, AnswerRefusal>
  /** Accepted holds whose operator is the viewer (agion/Agion#3435) */
  operatorHolds: Escalation[]
  /** Hold IDs with an in-flight Take over */
  acceptingIds: Set<string>
  /** The last refusal of a Take over, per hold ID */
  acceptRefusals: Record<string, TakeOverRefusal>

  loadEscalations: (filters?: EscalationListRequest) => Promise<void>
  loadPendingCount: () => Promise<void>
  resolveEscalation: (id: string, decision: 'approved' | 'denied', reason: string) => Promise<void>
  /**
   * Send the answer of a question escalation. A single choice goes to
   * governance. A free text or form goes to the run (owner ruling 2026-10-25): a
   * ticket from governance, the answer on the status socket through the
   * relay, then governance is read until the run has settled it; the answer
   * itself is neither kept nor logged. A refusal is kept in answerRefusals
   * instead of thrown; ESCALATION_ALREADY_ANSWERED also re-reads the
   * escalation so the recorded answer shows.
   */
  answerEscalation: (id: string, answer: EscalationAnswer) => Promise<void>
  /** Re-read one escalation and put it over the stored one. */
  refreshEscalation: (id: string) => Promise<void>
  /**
   * Take over a pending hold as operatorId. Resolves true when governance
   * accepted it; a refusal is kept in acceptRefusals instead of thrown.
   */
  acceptHandoff: (id: string, operatorId: string) => Promise<boolean>
  /** Hand a hold back to the agent; resolves true when governance took it back. */
  returnHandoff: (id: string) => Promise<boolean>
  /** Read the accepted holds whose operator is operatorId. */
  loadOperatorHolds: (operatorId: string) => Promise<void>
  /**
   * Re-read one hold: kept while governance reports it accepted with
   * operatorId as its operator, dropped otherwise.
   */
  refreshOperatorHold: (id: string, operatorId: string) => Promise<void>
  clearError: () => void
}

export const useEscalationStore = create<EscalationStore>()(
  subscribeWithSelector((set, get) => ({
    escalations: [],
    pendingCount: 0,
    isLoading: false,
    error: null,
    resolvingIds: new Set<string>(),
    answeringIds: new Set<string>(),
    answerRefusals: {},
    operatorHolds: [],
    acceptingIds: new Set<string>(),
    acceptRefusals: {},

    loadEscalations: async (filters?: EscalationListRequest) => {
      set({ isLoading: true, error: null })
      try {
        const response = await escalationApi.list(filters)
        set({
          escalations: response.escalations,
          pendingCount: response.escalations.filter((e) => e.status === 'pending').length,
          isLoading: false,
        })
      } catch (error) {
        console.error('Failed to load escalations:', error)
        set({
          error: error instanceof Error ? error.message : 'Failed to load escalations',
          isLoading: false,
        })
      }
    },

    loadPendingCount: async () => {
      try {
        const count = await escalationApi.getPendingCount()
        set({ pendingCount: count })
      } catch (error) {
        // Non-critical — keep the last known count
        console.error('Failed to load pending escalation count:', error)
      }
    },

    resolveEscalation: async (id: string, decision: 'approved' | 'denied', reason: string) => {
      set((state) => ({ resolvingIds: new Set(state.resolvingIds).add(id), error: null }))
      try {
        await escalationApi.resolve(id, { decision, reason })
        set((state) => {
          const resolvingIds = new Set(state.resolvingIds)
          resolvingIds.delete(id)
          return {
            escalations: state.escalations.map((e) =>
              e.id === id
                ? { ...e, status: decision, approval_reason: reason, resolved_at: new Date().toISOString() }
                : e,
            ),
            pendingCount: Math.max(0, state.pendingCount - 1),
            resolvingIds,
          }
        })
      } catch (error) {
        console.error('Failed to resolve escalation:', error)
        set((state) => {
          const resolvingIds = new Set(state.resolvingIds)
          resolvingIds.delete(id)
          return {
            error: error instanceof Error ? error.message : 'Failed to resolve escalation',
            resolvingIds,
          }
        })
        throw error
      }
    },

    answerEscalation: async (id: string, answer: EscalationAnswer) => {
      set((state) => {
        const answerRefusals = { ...state.answerRefusals }
        delete answerRefusals[id]
        return { answeringIds: new Set(state.answeringIds).add(id), answerRefusals }
      })
      const done = (state: EscalationStore) => {
        const answeringIds = new Set(state.answeringIds)
        answeringIds.delete(id)
        return answeringIds
      }
      if (answerGoesToRun(answer)) {
        const refused = await answerOnTheRun(id, answer, (fresh) =>
          set((state) => ({ escalations: upsertEscalation(state.escalations, fresh) })),
        )
        set((state) =>
          refused
            ? { answeringIds: done(state), answerRefusals: { ...state.answerRefusals, [id]: refused } }
            : { answeringIds: done(state), pendingCount: Math.max(0, state.pendingCount - 1) },
        )
        if (refused === 'already_answered') await get().refreshEscalation(id)
        return
      }
      let refusal: AnswerRefusal | undefined
      let stored: Escalation | undefined
      try {
        stored = await escalationApi.answer(id, { answer })
      } catch (error) {
        console.error('Failed to answer escalation:', error)
        refusal = answerRefusalOf(error)
      }
      if (refusal) {
        const kind = refusal
        set((state) => ({
          answeringIds: done(state),
          answerRefusals: { ...state.answerRefusals, [id]: kind },
        }))
        if (kind === 'already_answered') await get().refreshEscalation(id)
        return
      }
      if (isEscalation(stored)) {
        const fresh = stored
        set((state) => ({
          escalations: mergeEscalation(state.escalations, fresh),
          pendingCount: Math.max(0, state.pendingCount - 1),
          answeringIds: done(state),
        }))
        return
      }
      set((state) => ({ answeringIds: done(state) }))
      await get().refreshEscalation(id)
    },

    refreshEscalation: async (id: string) => {
      try {
        const fresh = await escalationApi.get(id)
        if (isEscalation(fresh)) {
          set((state) => ({ escalations: mergeEscalation(state.escalations, fresh) }))
        }
      } catch (error) {
        // The item keeps its last known state; the next poll reads it again.
        console.error('Failed to refresh escalation:', error)
      }
    },

    acceptHandoff: async (id: string, operatorId: string) => {
      set((state) => {
        const acceptRefusals = { ...state.acceptRefusals }
        delete acceptRefusals[id]
        return { acceptingIds: new Set(state.acceptingIds).add(id), acceptRefusals }
      })
      const done = (state: EscalationStore) => {
        const acceptingIds = new Set(state.acceptingIds)
        acceptingIds.delete(id)
        return acceptingIds
      }
      try {
        await escalationApi.accept(id)
      } catch (error) {
        console.error('Failed to take over the conversation:', error)
        const refusal = takeOverRefusalOf(error)
        set((state) => ({ acceptingIds: done(state), acceptRefusals: { ...state.acceptRefusals, [id]: refusal } }))
        return false
      }
      set((state) => ({
        acceptingIds: done(state),
        escalations: state.escalations.filter((e) => e.id !== id),
        pendingCount: Math.max(0, state.pendingCount - 1),
      }))
      await get().refreshOperatorHold(id, operatorId)
      return true
    },

    returnHandoff: async (id: string) => {
      try {
        await escalationApi.returnHandoff(id)
      } catch (error) {
        console.error('Failed to hand the conversation back:', error)
        return false
      }
      set((state) => ({ operatorHolds: state.operatorHolds.filter((e) => e.id !== id) }))
      return true
    },

    loadOperatorHolds: async (operatorId: string) => {
      try {
        const response = await escalationApi.list({ status: 'approved', limit: 50 })
        set({
          operatorHolds: response.escalations.filter((e) => isOperatorHold(e) && e.operator_id === operatorId),
        })
      } catch (error) {
        // The holds keep their last known state; the next poll reads them again.
        console.error('Failed to load held conversations:', error)
      }
    },

    refreshOperatorHold: async (id: string, operatorId: string) => {
      try {
        const fresh = await escalationApi.get(id)
        const held = isEscalation(fresh) && fresh.status === 'approved' && isOperatorHold(fresh) && fresh.operator_id === operatorId
        set((state) => {
          const others = state.operatorHolds.filter((e) => e.id !== id)
          return { operatorHolds: held ? [...others, fresh] : others }
        })
      } catch (error) {
        console.error('Failed to refresh the held conversation:', error)
      }
    },

    clearError: () => set({ error: null }),
  })),
)

// =============================================================================
// SELECTOR HOOKS
// =============================================================================

/** Pending escalations, oldest expiry first (most urgent at the top).
 *  Filtering happens in useMemo, not the selector — zustand v5 selectors
 *  must return stable references. */
export function usePendingEscalations(): Escalation[] {
  const escalations = useEscalationStore((state) => state.escalations)
  return useMemo(
    () =>
      escalations
        .filter((e) => e.status === 'pending')
        .sort((a, b) => Date.parse(a.expires_at) - Date.parse(b.expires_at)),
    [escalations],
  )
}
